<script lang="ts">
    import { onMount } from "svelte";
    import { userData, userSignedIn } from "../store";
    import {
        getPromoDatabase,
        createPromo,
        deactivatePromo,
        getTransactionSummaryDatabase,
        getSubscriptionSummaryDatabase,
    } from "../firebaseOperations";

    let code = "";
    let type = "";
    let value = "";
    let expirationdate = "";
    let typeOptions = ["credits", "premium"];
    let transactions = [];
    let subscriptions = [];
    let days = [];
    let currentMonth = new Date().getMonth();
    let currentYear = new Date().getFullYear();
    let daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    let promoCodesLoaded = false;
    let subscriptionSummaryLoaded = false;

    // Reactive statement that watches for changes in $userData.admin
    $: if ($userData.admin && !promoCodesLoaded && !subscriptionSummaryLoaded) {
        getPromoCodes();
        getSubscriptionSummary();
        promoCodesLoaded = true;
        subscriptionSummaryLoaded = true;
    }

    async function getSubscriptionSummary() {
        if (!$userSignedIn) {
            return;
        }
        let user_id = $userData.id;
        let transactionSummary = await getTransactionSummaryDatabase(user_id);
        let results = await getSubscriptionSummaryDatabase(user_id);
        // Filter out subscriptions with 79.99 price
        results[1]["subscriptionList"] = results[1]["subscriptionList"].filter(
            (subscription) => subscription.price !== 79.99
        );

        const transactionsJSON = parseTransactionSummary(transactionSummary);
        transactionsJSON.forEach(transaction => {
            if (transaction.description === "Credit Purchase" || transaction.description === "Subscription creation" || transaction.description === "Subscription renewal") {
                transactions.push(transaction);
            }
        });
        subscriptions = results[1].subscriptionList || [];
        updateCalendar();
        document.getElementById("transactionSummary").innerHTML = transactionSummary;
    }

    function parseTransactionSummary(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const rows = doc.querySelectorAll("table tr");
        const transactions = [];

        rows.forEach((row, index) => {
            if (index === 0) return;
            const cells = row.querySelectorAll("td");
            const date = cells[0].textContent.trim();
            const amount = parseFloat(cells[1].textContent.trim().replace('$', ''));
            const description = cells[2].textContent.trim();
            const account = cells[3].textContent.trim();

            transactions.push({
                date,
                amount,
                description,
                account
            });
        });

        return transactions;
    }

    function updateCalendar() {
        days = []; // Reset days array

        const today = new Date();

        for (let day = 1; day <= daysInMonth; day++) {
            const currentDate = new Date(currentYear, currentMonth, day);
            const isToday =
                currentDate.getDate() === today.getDate() &&
                currentDate.getMonth() === today.getMonth() &&
                currentDate.getFullYear() === today.getFullYear();
            const isPast = currentDate < today;

            let earnings = 0;
            let transactionsForDay = [];

            // Add transactions earnings
            transactions.forEach((transaction) => {
                const transactionDate = new Date(transaction.date);
                if (transactionDate.getDate() === day && transactionDate.getMonth() === currentMonth && transactionDate.getFullYear() === currentYear) {
                    earnings += transaction.amount;
                    transactionsForDay.push(transaction);
                }
            });

            // Add subscription renewals earnings (including future dates)
            subscriptions.forEach((subscription) => {
                const renewalDate = new Date(subscription.renewal);
                if (renewalDate.getDate() === day && renewalDate.getMonth() === currentMonth && renewalDate.getFullYear() === currentYear) {
                    earnings += subscription.profit;
                    transactionsForDay.push({
                        date: renewalDate.toISOString(),
                        amount: subscription.profit,
                        description: "Subscription Renewal",
                        account: ""
                    });
                }
            });

            days.push({
                day,
                date: currentDate,
                isToday,
                isPast,
                earnings,
                transactions: transactionsForDay,
            });
        }
    }

    async function getPromoCodes() {
        if (!$userSignedIn) {
            return;
        }
        let table = await getPromoDatabase($userData.id);
        document.getElementById("promoCodes").innerHTML = table;
    }

    async function createPromoCode() {
        if (!$userSignedIn) {
            return;
        }
        let data = await createPromo(code, type, value, expirationdate);
        document.getElementById("result").innerHTML = "Code created: " + data.id;
        setTimeout(() => {
            document.getElementById("result").innerHTML = "";
        }, 5000);
    }

    async function deactivatePromoCode() {
        if (!$userSignedIn) {
            return;
        }
        let user = $userData.id;
        const data = await deactivatePromo(code, user);
        document.getElementById("result").innerHTML = data.message;
        setTimeout(() => {
            document.getElementById("result").innerHTML = "";
        }, 5000);
    }

</script>

<style>
    #calendar {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        border-collapse: collapse;
    }
    .day {
        padding: 10px;
        border: 1px solid white;
        text-align: center;
        font-family: Arial, sans-serif;
        font-size: 14px;
        position: relative;
        height: 50px;
        cursor: pointer;
    }
    .date-number {
        position: absolute !important;
        top: 5px;
        right: 5px;
        font-weight: bold;
    }
    .earnings {
        margin-top: 20px;
        font-size: 16px;
        font-weight: bold;
    }
    .overlay {
        display: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 150%;
        height: 215%;
        background-color: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 10px;
        z-index: 10;
        text-align: left;
        overflow-y: auto;
    }
    .overlay-content {
        height: 100%;
        overflow-y: auto;
    }
    .overlay h4 {
        margin-top: 0;
    }
    .month-bar {
        text-align: center;
        padding: 15px;
        background-color: white;
        font-size: 18px;
        font-weight: bold;
    }
    .day:hover .overlay {
        display: block;
    }
</style>

{#if $userData.admin}
    <div class="newSection">
        <div id="shop">
            <h3 style="text-align: center;">Admin Only</h3>

            <button on:click={getSubscriptionSummary}>Get Sub Summary</button>
            <div class="month-bar">
                {new Date(currentYear, currentMonth).toLocaleString("default", { month: "long" })} {currentYear} Earnings (Both Accounts)
            </div>
            <div id="calendar">
                {#each days as dayData}
                    <div class="day"
                         style="background-color: {dayData.isToday ? '#cfc130' : dayData.isPast ? '#30cf65' : '#30b0cf'}">
                        <div class="date-number">{dayData.day}</div>
                        <div class="earnings">${dayData.earnings.toFixed(2)}</div>

                        {#if dayData.transactions.length > 0}
                            <div class="overlay">
                                <div class="overlay-content">
                                    <h4>Transactions for {dayData.date.toDateString()}</h4>
                                    {#each dayData.transactions as t}
                                        <p>{t.description}: ${t.amount.toFixed(2)}</p>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                    </div>
                {/each}
            </div>
            <br>
            <div id="transactionSummary"></div>
            <br />
            <button on:click={getPromoCodes}>Get Promo Codes</button>
            <br /><br />
            <p id="promoCodes"></p>
            <br />
            <input bind:value={code} placeholder="Code" />
            <br />
            <select bind:value={type}>
                <option value="" disabled selected>Select Type</option>
                {#each typeOptions as option}
                    <option value={option}>{option}</option>
                {/each}
            </select>
            <br />
            <input bind:value={value} placeholder="Value" />
            <br />
            <input type="datetime-local" bind:value={expirationdate} placeholder="Expiration" />
            <br />
            <button on:click={createPromoCode}>Create Promo Code</button>
            <br /><br />
            <input bind:value={code} placeholder="Code" />
            <br />
            <button on:click={deactivatePromoCode}>Deactivate Promo Code</button>
            <br />
            <p id="result"></p>
            <br />
        </div>
    </div>
{/if}
